import React, { useState, useEffect } from 'react';
import classes from './WeeklyNettraffic.module.css';

const IPTables = ({ internalIPs, externalIPs }) => {
  const [internalPage, setInternalPage] = useState(1);
  const [externalPage, setExternalPage] = useState(1);
  const [internalSearch, setInternalSearch] = useState('');
  const [externalSearch, setExternalSearch] = useState('');
  const [filteredInternalIPs, setFilteredInternalIPs] = useState([]);
  const [filteredExternalIPs, setFilteredExternalIPs] = useState([]);
  const rowsPerPage = 10;

  // Filter IPs based on search term
  useEffect(() => {
    if (internalIPs && internalIPs.length > 0) {
      const filtered = internalIPs.filter(ip => 
        ip.ip.toLowerCase().includes(internalSearch.toLowerCase())
      );
      setFilteredInternalIPs(filtered);
      setInternalPage(1); // Reset to first page when search changes
    } else {
      setFilteredInternalIPs([]);
    }
  }, [internalSearch, internalIPs]);

  useEffect(() => {
    if (externalIPs && externalIPs.length > 0) {
      const filtered = externalIPs.filter(ip => 
        ip.ip.toLowerCase().includes(externalSearch.toLowerCase()) ||
        (ip.country && ip.country.toLowerCase().includes(externalSearch.toLowerCase()))
      );
      setFilteredExternalIPs(filtered);
      setExternalPage(1); // Reset to first page when search changes
    } else {
      setFilteredExternalIPs([]);
    }
  }, [externalSearch, externalIPs]);

  if (!internalIPs || !externalIPs || (internalIPs.length === 0 && externalIPs.length === 0)) {
    return (
      <div className={classes.noDataTables}>
        <p>No IP address data available</p>
      </div>
    );
  }

  // Calculate pagination for internal IPs
  const internalTotalPages = Math.ceil(filteredInternalIPs.length / rowsPerPage);
  const internalStartIndex = (internalPage - 1) * rowsPerPage;
  const internalEndIndex = internalStartIndex + rowsPerPage;
  const paginatedInternalIPs = filteredInternalIPs.slice(internalStartIndex, internalEndIndex);

  // Calculate pagination for external IPs
  const externalTotalPages = Math.ceil(filteredExternalIPs.length / rowsPerPage);
  const externalStartIndex = (externalPage - 1) * rowsPerPage;
  const externalEndIndex = externalStartIndex + rowsPerPage;
  const paginatedExternalIPs = filteredExternalIPs.slice(externalStartIndex, externalEndIndex);

  // Handle page changes
  const handleInternalPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= internalTotalPages) {
      setInternalPage(newPage);
    }
  };

  const handleExternalPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= externalTotalPages) {
      setExternalPage(newPage);
    }
  };

  // Render pagination controls
  const renderPagination = (currentPage, totalPages, onPageChange) => {
    return (
      <div className={classes.pagination}>
        <button 
          onClick={() => onPageChange(1)} 
          disabled={currentPage === 1}
          className={classes.paginationButton}
        >
          &laquo;
        </button>
        <button 
          onClick={() => onPageChange(currentPage - 1)} 
          disabled={currentPage === 1}
          className={classes.paginationButton}
        >
          &lsaquo;
        </button>
        <span className={classes.paginationInfo}>
          Page {currentPage} of {totalPages || 1}
        </span>
        <button 
          onClick={() => onPageChange(currentPage + 1)} 
          disabled={currentPage === totalPages || totalPages === 0}
          className={classes.paginationButton}
        >
          &rsaquo;
        </button>
        <button 
          onClick={() => onPageChange(totalPages)} 
          disabled={currentPage === totalPages || totalPages === 0}
          className={classes.paginationButton}
        >
          &raquo;
        </button>
      </div>
    );
  };

  return (
    <div className={classes.ipTablesContainer}>
      <div className={classes.ipTable}>
        <h3 className={classes.tableTitle}>
          Internal IP Addresses 
          <span className={classes.tableCount}>
            (Showing {paginatedInternalIPs.length} of {filteredInternalIPs.length})
          </span>
        </h3>
        <div className={classes.searchContainer}>
          <input
            type="text"
            placeholder="Search internal IPs..."
            value={internalSearch}
            onChange={(e) => setInternalSearch(e.target.value)}
            className={classes.searchInput}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Connection Count</th>
            </tr>
          </thead>
          <tbody>
            {paginatedInternalIPs.length > 0 ? (
              paginatedInternalIPs.map((ip, index) => (
                <tr key={index}>
                  <td>{ip.ip}</td>
                  <td>{ip.count.toLocaleString()}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2" className={classes.noData}>No matching internal IPs found</td>
              </tr>
            )}
          </tbody>
        </table>
        {internalTotalPages > 1 && renderPagination(internalPage, internalTotalPages, handleInternalPageChange)}
      </div>

      <div className={classes.ipTable}>
        <h3 className={classes.tableTitle}>
          External IP Addresses
          <span className={classes.tableCount}>
            (Showing {paginatedExternalIPs.length} of {filteredExternalIPs.length})
          </span>
        </h3>
        <div className={classes.searchContainer}>
          <input
            type="text"
            placeholder="Search external IPs or countries..."
            value={externalSearch}
            onChange={(e) => setExternalSearch(e.target.value)}
            className={classes.searchInput}
          />
        </div>
        <table>
          <thead>
            <tr>
              <th>IP Address</th>
              <th>Connection Count</th>
              <th>Country</th>
            </tr>
          </thead>
          <tbody>
            {paginatedExternalIPs.length > 0 ? (
              paginatedExternalIPs.map((ip, index) => (
                <tr key={index}>
                  <td>{ip.ip}</td>
                  <td>{ip.count.toLocaleString()}</td>
                  <td>{ip.country}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className={classes.noData}>No matching external IPs found</td>
              </tr>
            )}
          </tbody>
        </table>
        {externalTotalPages > 1 && renderPagination(externalPage, externalTotalPages, handleExternalPageChange)}
      </div>
    </div>
  );
};

export default IPTables;
